import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Bar } from 'react-chartjs-2';
import apiService from '../services/api';
import { useChartTheme, tooltipStyle, withAlpha } from './chartSetup';
import { AnimatedNumber, Reveal } from './motion';
import {
  Container,
  Card,
  Eyebrow,
  SectionTitle,
  Lead,
  SectionHeader,
  Segmented,
  SegmentedItem,
  TextLink,
  Rate,
  rateTone,
  EmptyState,
  IconChevronRight,
} from './ui';

// Landing-page chart built from the latest daily snapshot (USDT-M perpetuals).
// If the API is unreachable the whole section stays hidden rather than showing an error.

const ASSETS = ['BTC', 'ETH', 'SOL'];
const PERIODS = ['1d', '30d', '90d', '365d'];

const Section = styled.section`
  padding-top: clamp(64px, 9vw, 112px);
`;

const Panel = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px clamp(18px, 3vw, 28px);
  border-bottom: 1px solid var(--separator);
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ChartArea = styled.div`
  position: relative;
  padding: 20px clamp(12px, 2.4vw, 24px) 16px;
  min-height: 320px;
`;

const Kpis = styled.dl`
  display: grid;
  grid-template-columns: 1fr;
  border-left: 1px solid var(--separator);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-left: 0;
    border-top: 1px solid var(--separator);
  }
`;

const Kpi = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 18px 24px;

  & + & {
    border-top: 1px solid var(--separator);
  }

  @media (max-width: 900px) {
    & + & {
      border-top: 0;
    }

    &:nth-child(even) {
      border-left: 1px solid var(--separator);
    }

    &:nth-child(n + 3) {
      border-top: 1px solid var(--separator);
    }
  }

  dt {
    font-size: 0.8125rem;
    color: var(--text-secondary);
  }

  dd {
    font-size: 1.375rem;
    font-weight: 650;
    letter-spacing: -0.022em;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  small {
    font-size: 0.8125rem;
    color: var(--text-tertiary);
  }
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  padding: 14px clamp(18px, 3vw, 28px);
  border-top: 1px solid var(--separator);
  background: var(--surface-muted);
  font-size: 0.8125rem;
  color: var(--text-tertiary);
  font-variant-numeric: tabular-nums;
`;

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
`;

const SkeletonBar = styled.div`
  height: 14px;
  margin: 14px 0;
  border-radius: 4px;
  width: ${(p) => p.$width}%;
  background: linear-gradient(90deg, var(--fill) 0%, var(--fill-strong) 50%, var(--fill) 100%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.6s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SKELETON_WIDTHS = [92, 78, 70, 61, 55, 47, 40, 33, 26];

const formatSigned = (value) => `${value > 0 ? '+' : ''}${value.toFixed(4)}%`;
const formatPlain = (value) => `${value.toFixed(4)}%`;

function MarketSnapshot({ locale }) {
  const theme = useChartTheme();
  const [asset, setAsset] = useState('BTC');
  const [period, setPeriod] = useState('30d');
  const [snapshots, setSnapshots] = useState({});
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (snapshots[asset]) {
      setStatus('ready');
      return undefined;
    }
    let cancelled = false;
    setStatus('loading');
    (async () => {
      try {
        const { dates = [] } = await apiService.getAvailableDates(asset);
        const response = await apiService.getDailyFundingData(asset, dates[0]);
        if (cancelled) return;
        if (!response || !Array.isArray(response.data) || response.data.length === 0) throw new Error('empty');
        setSnapshots((prev) => ({ ...prev, [asset]: { date: dates[0], ...response } }));
        setStatus('ready');
      } catch (error) {
        if (cancelled) return;
        // Nothing loaded yet: hide the section. Otherwise keep it and mark just this asset as empty.
        if (Object.keys(snapshots).length) {
          setSnapshots((prev) => ({ ...prev, [asset]: { date: null, data: [] } }));
          setStatus('ready');
        } else {
          setStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [asset, snapshots]);

  const snapshot = snapshots[asset];

  const rows = useMemo(() => {
    if (!snapshot) return [];
    return snapshot.data
      .filter((item) => String(item.contract_type).toLowerCase() === 'usdt-m')
      .map((item) => ({
        name: item.exchange_name,
        value: (item[`period_${period}`]?.average_funding_rate ?? NaN) * 100,
        records: item[`period_${period}`]?.total_records ?? 0,
      }))
      .filter((row) => Number.isFinite(row.value))
      .sort((a, b) => b.value - a.value);
  }, [snapshot, period]);

  const stats = useMemo(() => {
    if (!rows.length) return null;
    const highest = rows[0];
    const lowest = rows[rows.length - 1];
    const average = rows.reduce((sum, row) => sum + row.value, 0) / rows.length;
    return { highest, lowest, spread: highest.value - lowest.value, average };
  }, [rows]);

  if (status === 'error') return null;

  const chartData = {
    labels: rows.map((row) => row.name),
    datasets: [
      {
        data: rows.map((row) => row.value),
        backgroundColor: rows.map((row) => withAlpha(row.value >= 0 ? theme.positive : theme.negative, 0.82)),
        hoverBackgroundColor: rows.map((row) => (row.value >= 0 ? theme.positive : theme.negative)),
        borderRadius: 4,
        borderSkipped: 'start',
        barThickness: 14,
      },
    ],
  };

  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltipStyle(theme),
        displayColors: false,
        callbacks: {
          label: (context) => formatSigned(context.parsed.x),
          afterLabel: (context) => `${locale.records}: ${rows[context.dataIndex]?.records ?? 0}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: (context) => (context.tick && context.tick.value === 0 ? theme.axis : theme.grid),
          drawTicks: false,
        },
        border: { display: false },
        ticks: {
          color: theme.textSecondary,
          font: { family: theme.font, size: 11 },
          padding: 8,
          callback: (value) => `${parseFloat(Number(value).toFixed(4))}%`,
        },
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: theme.text, font: { family: theme.font, size: 12, weight: '500' }, padding: 8 },
      },
    },
  };

  const chartHeight = Math.max(280, rows.length * 30 + 40);

  return (
    <Section id="market" aria-labelledby="market-title">
      <Container>
        <SectionHeader>
          <Eyebrow>{locale.eyebrow}</Eyebrow>
          <SectionTitle id="market-title">{locale.title}</SectionTitle>
          <Lead>{locale.lead}</Lead>
        </SectionHeader>

        <Reveal>
          <Panel>
            <Toolbar>
              <Segmented role="group" aria-label={locale.asset}>
                {ASSETS.map((item) => (
                  <SegmentedItem
                    key={item}
                    type="button"
                    $active={asset === item}
                    aria-pressed={asset === item}
                    onClick={() => setAsset(item)}
                  >
                    {item}
                  </SegmentedItem>
                ))}
              </Segmented>
              <Segmented role="group" aria-label={locale.period}>
                {PERIODS.map((item) => (
                  <SegmentedItem
                    key={item}
                    type="button"
                    $active={period === item}
                    aria-pressed={period === item}
                    onClick={() => setPeriod(item)}
                  >
                    {locale.periods[item]}
                  </SegmentedItem>
                ))}
              </Segmented>
            </Toolbar>

            <Body>
              <ChartArea aria-busy={status === 'loading'}>
                {status === 'loading' && !rows.length ? (
                  SKELETON_WIDTHS.map((width) => <SkeletonBar key={width} $width={width} />)
                ) : !rows.length ? (
                  <EmptyState>
                    <p>{locale.noData}</p>
                  </EmptyState>
                ) : (
                  <div style={{ height: chartHeight }}>
                    <Bar data={chartData} options={chartOptions} aria-label={locale.title} role="img" />
                  </div>
                )}
              </ChartArea>

              <Kpis>
                <Kpi>
                  <dt>{locale.highest}</dt>
                  <dd>
                    <Rate $tone={stats ? rateTone(stats.highest.value) : 'neutral'}>
                      {stats ? <AnimatedNumber value={stats.highest.value} format={formatSigned} /> : '—'}
                    </Rate>
                  </dd>
                  <small>{stats ? stats.highest.name : ' '}</small>
                </Kpi>
                <Kpi>
                  <dt>{locale.lowest}</dt>
                  <dd>
                    <Rate $tone={stats ? rateTone(stats.lowest.value) : 'neutral'}>
                      {stats ? <AnimatedNumber value={stats.lowest.value} format={formatSigned} /> : '—'}
                    </Rate>
                  </dd>
                  <small>{stats ? stats.lowest.name : ' '}</small>
                </Kpi>
                <Kpi>
                  <dt>{locale.spread}</dt>
                  <dd>{stats ? <AnimatedNumber value={stats.spread} format={formatPlain} /> : '—'}</dd>
                  <small>{locale.spreadNote}</small>
                </Kpi>
                <Kpi>
                  <dt>{locale.average}</dt>
                  <dd>
                    <Rate $tone={stats ? rateTone(stats.average) : 'neutral'}>
                      {stats ? <AnimatedNumber value={stats.average} format={formatSigned} /> : '—'}
                    </Rate>
                  </dd>
                  <small>{locale.averageNote.replace('{count}', rows.length)}</small>
                </Kpi>
              </Kpis>
            </Body>

            <Footer>
              <span>
                {snapshot && snapshot.date
                  ? locale.snapshot.replace('{date}', snapshot.date || '').replace('{period}', locale.periodLong[period])
                  : ' '}
              </span>
              <TextLink as={Link} to="/daily-data">
                {locale.cta}
                <IconChevronRight size={15} strokeWidth={2} />
              </TextLink>
            </Footer>
          </Panel>
        </Reveal>
      </Container>
    </Section>
  );
}

export default MarketSnapshot;
